import styled, { useTheme } from "styled-components";
import { Skeleton } from "@mui/material";
import { isMobile } from "react-device-detect";

import edit from "./assets/ic-edit.svg";

const PlotContainer = styled.div`
  min-width: ${isMobile ? "100%" : "380px"};
  display: inline;
`;

const Title = styled.label`
  display: block;
  min-width: ${isMobile ? "100%" : "380px"};
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 32px;
  font-size: ${(props) => props.theme.font.size.primary};
  font-weight: 600;
  color: ${(props) => props.theme.color.text.primary};
`;

const StatusContainer = styled.div`
  padding-top: 16px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const IndicatorContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: start;
`;

const StatusIndicator = styled.div`
  height: 12px;
  width: 12px;
  background-color: ${(props) => props.color};
  border-radius: 50%;
  display: inline-block;
`;

const StatusTitle = styled.div`
  margin-top: 0px;
  margin-left: 10px;
  font-size: ${(props) => props.theme.font.size.disclaimer};
  font-weight: 600;
  color: ${(props) => props.color};
`;

const EditContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  cursor: pointer;
  &:hover {
    opacity: 90%;
  }
`;

const EditTitle = styled.span`
  color: ${(props) => props.theme.color.accent};
  font-size: ${(props) => props.theme.font.size.small};
  font-weight: 600;
`;

const EditIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const Status = (props) => (
  <StatusContainer>
    <IndicatorContainer>
      <StatusIndicator color={props.status.color} />
      <StatusTitle color={props.status.color}>{props.status.title}</StatusTitle>
    </IndicatorContainer>

    {props.editable && (
      <EditContainer onClick={props.onEdit}>
        <EditTitle>Edit</EditTitle>
        <EditIcon src={edit}></EditIcon>
      </EditContainer>
    )}
  </StatusContainer>
);

const ZoneContainer = styled.div`
  padding-top: 14px;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const ZoneIcon = styled.img`
  width: 40px;
  height: 40px;
`;

const ZoneTitle = styled.span`
  padding-left: 12px;
  font-size: ${(props) => props.theme.font.size.disclaimer};
  font-weight: 600;
  color: ${(props) => props.theme.color.text.primary};
`;

const Zone = (props) => {
  return (
    <ZoneContainer>
      <ZoneIcon src={props.icon} />
      <ZoneTitle>{props.children}</ZoneTitle>
    </ZoneContainer>
  );
};

const Spacer = styled.div`
  width: 100%;
  border-top: 1px solid white;
  opacity: 12%;
  margin-top: 16px;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: start;
  margin-top: 4px;
`;

const InfoText = styled.span`
  margin-top: 12px;
  text-align: start;
  font-size: ${(props) => props.theme.font.size.disclaimer};
  font-weight: ${(props) => props.weight || "600"};
  color: ${(props) => props.color};
`;

const Info = (props) => {
  const { color } = useTheme();

  return (
    <InfoContainer>
      <InfoText color={color.text.caption}>{props.caption}</InfoText>
      <InfoText
        color={color.text.primary}
        size={props.value && props.value.size}
        weight={props.value && props.value.weight}
      >
        {props.children}
      </InfoText>
    </InfoContainer>
  );
};

const Price = (props) => {
  const { color } = useTheme();

  return (
    <InfoContainer>
      <InfoText color={color.text.caption}>{props.caption}</InfoText>
      <InfoText color={color.accent} size="24px">
        {props.children}
      </InfoText>
    </InfoContainer>
  );
};

const Fees = (props) => {
  const { color } = useTheme();

  return (
    <InfoContainer>
      <InfoText color={color.text.caption} size="14px" weight="500">
        {props.caption}
      </InfoText>
      <InfoText color={color.text.primary} size="14px" weight="500">
        {props.children}
      </InfoText>
    </InfoContainer>
  );
};

const Mint = styled.button`
  margin-top: 16px;
  height: ${(props) => props.theme.coloredButtonHeight.primary};
  width: 100%;
  border-width: 0;
  border-radius: 10px;
  background-color: ${(props) => props.theme.color.accent};
  color: ${(props) => props.theme.color.text.secondary};
  font-size: ${(props) => props.theme.font.size.secondary};
  font-weight: 600;
  cursor: pointer;
  &:hover {
    opacity: 90%;
  }
  &:disabled {
    opacity: 60%;
    cursor: not-allowed;
  }
`;

const TitleSkeleton = styled(Skeleton)({
  marginRight: 36,
  "&.MuiSkeleton-root": {
    width: `${isMobile ? "90%" : "360px"}`,
    variant: "rectangle",
    height: "30px",
    backgroundColor: (props) => props.theme.color.background.tertiary,
  },
});

const StatusSkeleton = styled(Skeleton)({
  "&.MuiSkeleton-root": {
    variant: "rectangle",
    height: "20px",
    width: `${isMobile ? "30%" : "100px"}`,
    backgroundColor: (props) => props.theme.color.background.tertiary,
  },
});

const ZoneIconSkeleton = styled(Skeleton)({
  marginRight: 16,
  "&.MuiSkeleton-root": {
    width: "30px",
    variant: "rectangle",
    height: "40px",
    backgroundColor: (props) => props.theme.color.background.tertiary,
  },
});

const ZoneTitleSkeleton = styled(Skeleton)({
  marginRight: 16,
  "&.MuiSkeleton-root": {
    width: "80px",
    variant: "rectangle",
    height: "40px",
    backgroundColor: (props) => props.theme.color.background.tertiary,
  },
});

const ZoneSkeleton = () => {
  return (
    <ZoneContainer>
      <ZoneIconSkeleton />
      <ZoneTitleSkeleton />
    </ZoneContainer>
  );
};

const InfoSkeletonSmall = styled(Skeleton)({
  "&.MuiSkeleton-root": {
    variant: "rectangle",
    width: `${isMobile ? "90%" : "320px"}`,
    height: "26px",
    backgroundColor: (props) => props.theme.color.background.tertiary,
  },
});

const InfoSkeletonLarge = styled(Skeleton)({
  "&.MuiSkeleton-root": {
    variant: "rectangle",
    width: `${isMobile ? "100%" : "380px"}`,
    height: "26px",
    backgroundColor: (props) => props.theme.color.background.tertiary,
  },
});

export {
  PlotContainer,
  Title,
  Status,
  Spacer,
  Zone,
  Info,
  Price,
  Fees,
  Mint,
  TitleSkeleton,
  StatusSkeleton,
  ZoneSkeleton,
  InfoSkeletonSmall,
  InfoSkeletonLarge,
};
